import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { PlayCircle } from "lucide-react";
import axios from "../../../../axios";
import { getLectures, getLectureVideo } from "./lecture-utils";

const Lectures = () => {
  const [selected, setSelected] = useState(null);
  const [playbackError, setPlaybackError] = useState(false);
  const { data: lectures = [], isPending, isError, error, refetch } = useQuery({
    queryKey: ["institute-lectures"],
    queryFn: async () => {
      const { data } = await axios.get("/institute/lectures");
      return getLectures(data);
    },
    retry: false,
  });

  return (
    <main className="min-h-screen bg-[#fbfafc] px-4 py-6 pb-24 text-left text-[#211629] sm:px-6 lg:pt-16">
      <header className="mb-6">
        <h1 className="text-2xl font-bold text-[#4B2D83]">Lectures</h1>
        <p className="mt-1 text-sm text-gray-500">Watch lectures shared by your institute.</p>
      </header>

      {isPending ? (
        <p role="status" className="py-10 text-center text-gray-500">Loading lectures...</p>
      ) : isError ? (
        <div role="alert" className="rounded-xl bg-white p-6">
          <p>{error?.response?.data?.message || "Unable to load lectures. Please try again."}</p>
          <button type="button" onClick={() => refetch()} className="mt-4 rounded-lg bg-[#4B2D83] px-4 py-2 text-white">Try again</button>
        </div>
      ) : !lectures.length ? (
        <div className="rounded-xl bg-white p-8 text-center text-gray-500">No lectures available yet.</div>
      ) : (
        <section aria-label="Available lectures">
          <h2 className="mb-3 text-lg font-semibold">All lectures ({lectures.length})</h2>
          <div className="grid items-start gap-4 md:grid-cols-2 xl:grid-cols-3">
            {lectures.map((activeLecture, index) => {
              const lectureKey = activeLecture._id || activeLecture.id || index;
              const isSelected = selected === lectureKey;
              const video = getLectureVideo(activeLecture.videoUrl, window.location.origin);
              const thumbnailUrl = activeLecture.thumbnailUrl?.trim() || video?.thumbnailUrl;
              const embedUrl = video?.type === "embed" ? new URL(video.url) : null;
              if (embedUrl) embedUrl.searchParams.set("autoplay", "1");
              return (
          <article key={lectureKey} className="min-w-0 overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">
            {!isSelected ? (
              <button
                type="button"
                onClick={() => { setSelected(lectureKey); setPlaybackError(false); }}
                aria-label={`Play ${activeLecture.name || "Untitled lecture"}`}
                className="block w-full text-left hover:bg-purple-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-[#4B2D83]"
              >
                <span className="relative flex aspect-video w-full items-center justify-center bg-purple-100 text-[#4B2D83]">
                  {thumbnailUrl && <img
                    src={thumbnailUrl}
                    alt={`${activeLecture.name || "Lecture"} thumbnail`}
                    loading="lazy"
                    onError={(event) => {
                      const image = event.currentTarget;
                      if (video?.thumbnailUrl && image.src !== video.thumbnailUrl) {
                        image.src = video.thumbnailUrl;
                      } else {
                        image.style.display = "none";
                      }
                    }}
                    className="absolute inset-0 h-full w-full object-cover"
                  />}
                  <span className="relative rounded-full bg-white/90 p-3"><PlayCircle size={36} /></span>
                </span>
                <span className="block p-4">
                  <span className="block break-words text-lg font-bold">{activeLecture.name || "Untitled lecture"}</span>
                  {activeLecture.subject && <span className="mt-1 block text-sm text-gray-500">{activeLecture.subject}</span>}
                  {activeLecture.durationMinutes > 0 && <span className="mt-1 block text-sm text-gray-500">{activeLecture.durationMinutes} min</span>}
                </span>
              </button>
            ) : (
              <>
            {video?.type === "embed" ? (
              <iframe
                key={video.url}
                src={embedUrl.href}
                title={activeLecture.name || "Lecture video"}
                className="aspect-video min-h-[200px] w-full border-0 bg-black"
                referrerPolicy="strict-origin-when-cross-origin"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
                allowFullScreen
              />
            ) : video ? (
              <>
                <video
                  key={video.url}
                  src={video.url}
                  poster={activeLecture.thumbnailUrl || undefined}
                  controls
                  autoPlay
                  playsInline
                  preload="metadata"
                  onError={() => setPlaybackError(true)}
                  className="aspect-video w-full bg-black"
                >Your browser does not support video playback.</video>
                {playbackError && (
                  <p role="alert" className="p-4 text-sm text-red-600">
                    This video could not be played. <a href={video.url} target="_blank" rel="noopener noreferrer" className="underline">Open video</a>
                  </p>
                )}
              </>
            ) : (
              <p className="flex aspect-video items-center justify-center bg-gray-100 p-6 text-gray-500">Video is not available for this lecture.</p>
            )}
            <div className="p-5">
              <h2 className="text-xl font-bold">{activeLecture.name || "Untitled lecture"}</h2>
              <div className="mt-2 flex flex-wrap gap-3 text-sm text-gray-500">
                {activeLecture.subject && <span>{activeLecture.subject}</span>}
                {activeLecture.batch && <span>Batch: {activeLecture.batch}</span>}
                {activeLecture.durationMinutes > 0 && <span>{activeLecture.durationMinutes} min</span>}
              </div>
              {activeLecture.description && <p className="mt-4 whitespace-pre-line text-sm leading-6 text-gray-600">{activeLecture.description}</p>}
            </div>
              </>
            )}
          </article>
              );
            })}
          </div>
        </section>
      )}
    </main>
  );
};

export default Lectures;
